import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProjects } from '../hooks/useProjects';
import { useXmlFiles } from '../hooks/useXmlFiles';
import { useDeliveryComparison } from '../hooks/useDeliveryComparison';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  ArrowLeft, Truck, FolderOpen, FileText, CheckCircle, Clock, 
  Upload, X, Loader2, AlertTriangle, Trash2, FileX, Download,
  ChevronDown, ChevronRight
} from 'lucide-react';
import DataPurgeDialog from '../components/DataPurgeDialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DELIVERY_IMAGE = "https://customer-assets.emergentagent.com/job_7952493e-ecb0-4483-a1aa-f19d77930300/artifacts/ekpk2yty_Delivery.png";

const DeliveryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const initialProjectId = location.state?.projectId;
  const initialProject = location.state?.project;

  const projects = useProjects();
  const ddn = useXmlFiles('DDN');
  const dml = useXmlFiles('DML');
  const {
    isComparing, comparisonResult, history, isLoadingHistory, selectedHistory,
    compare, viewDetail, deleteItem, clearResult, downloadExport, exportHistoryItem
  } = useDeliveryComparison();

  const [selectedProject, setSelectedProject] = useState(initialProjectId || 'none');
  const [currentProjectName, setCurrentProjectName] = useState(initialProject?.title || null);
  const [sourceCollapsed, setSourceCollapsed] = useState(false);
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);

  // Log tool usage
  useEffect(() => {
    if (token) {
      axios.post(`${API}/history/tool-usage`, { tool_name: 'Delivery' }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
  }, []);

  const handleCompare = async () => {
    const ok = await compare(selectedProject, ddn.files, dml.files);
    if (ok) {
      ddn.clear();
      dml.clear();
      setSourceCollapsed(true);
      setShowPurgeDialog(true);
    }
  };

  const handleViewDetail = async (item) => {
    const ok = await viewDetail(item);
    if (ok) setSourceCollapsed(true);
  };

  const handleClear = () => {
    ddn.clear();
    dml.clear();
    clearResult();
    setSourceCollapsed(false);
  };

  const handleDismissResults = () => {
    clearResult();
    setSourceCollapsed(false);
  };

  return (
    <div className="space-y-8" data-testid="delivery-page">
      {/* Hidden file inputs */}
      <input type="file" ref={ddn.inputRef} onChange={ddn.handleSelect} accept=".xml" multiple className="hidden" />
      <input type="file" ref={dml.inputRef} onChange={dml.handleSelect} accept=".xml" multiple className="hidden" />

      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(selectedProject !== 'none' ? `/dashboard/projects/${selectedProject}` : '/dashboard/projects')} className="text-slate-600 hover:text-slate-900" data-testid="back-to-tools-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />{selectedProject !== 'none' ? 'Back to Project' : 'Back to Projects'}
        </Button>
      </div>

      {/* Tool Header */}
      <div className="flex flex-col md:flex-row items-start gap-6">
        <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4 shadow-lg">
          <img src={DELIVERY_IMAGE} alt="Delivery Tool" className="w-full h-full object-contain" />
        </div>
        <div className="space-y-2 flex-1">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Delivery Tool</h1>
          <p className="text-slate-600 max-w-2xl">
            Compare DDN (Data Dispatch Notice) files with DML (Data Module List) files to validate 
            deliveries and ensure consistency in your S1000D data packages.
          </p>
        </div>
      </div>

      {/* Project Selection */}
      <Card className={`border-2 ${selectedProject === 'none' ? 'border-violet-300 bg-violet-50/30' : 'border-slate-200'}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-slate-700">Select Project {!isAdmin && <span className="text-red-500">*</span>}</label>
            <Select value={selectedProject} onValueChange={(value) => { setSelectedProject(value); setCurrentProjectName(projects.find(p => p.id === value)?.title || null); }}>
              <SelectTrigger className="w-[300px]" data-testid="project-select"><SelectValue placeholder="Select a project..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none" className="text-slate-400">{isAdmin ? '-- No Project (Testing) --' : '-- Select a project --'}</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
              </SelectContent>
            </Select>
            {selectedProject !== 'none' && currentProjectName && <Badge variant="secondary" className="bg-violet-100 text-violet-700">{currentProjectName}</Badge>}
            {isAdmin && selectedProject === 'none' && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">Testing Mode</Badge>}
          </div>
          {!isAdmin && selectedProject === 'none' && <p className="text-sm text-violet-700 mt-2">A project must be selected to compare files and save history</p>}
        </CardContent>
      </Card>

      {/* Compare Button */}
      <div className="flex items-center gap-3">
        <Button className="bg-slate-900 hover:bg-slate-800 px-8 py-5 text-base" disabled={isComparing || ddn.files.length === 0 || dml.files.length === 0 || (!isAdmin && selectedProject === 'none')} onClick={handleCompare} data-testid="compare-btn">
          {isComparing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Comparing...</> : <><Truck className="w-4 h-4 mr-2" />Compare Files</>}
        </Button>
        {(ddn.files.length > 0 || dml.files.length > 0) && <Button variant="outline" onClick={handleClear}>Clear All</Button>}
        {!isAdmin && selectedProject === 'none' && <p className="text-sm text-violet-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Select a project first</p>}
        {(isAdmin || selectedProject !== 'none') && (ddn.files.length === 0 || dml.files.length === 0) && <p className="text-sm text-slate-500 flex items-center gap-2">Select both DDN and DML files to enable comparison</p>}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Source Selection - Collapsible */}
        <Card className="lg:col-span-2 border-slate-200">
          <CardHeader className="cursor-pointer select-none" onClick={() => setSourceCollapsed(!sourceCollapsed)}>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {sourceCollapsed ? <ChevronRight className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              <Truck className="w-5 h-5 text-violet-600" />Source Selection
              {sourceCollapsed && ddn.files.length > 0 && dml.files.length > 0 && <span className="text-sm font-normal text-slate-500 ml-2">({ddn.files.length} DDN, {dml.files.length} DML)</span>}
            </CardTitle>
            {!sourceCollapsed && <CardDescription>Upload DDN and DML XML files for comparison</CardDescription>}
          </CardHeader>
          {!sourceCollapsed && (
            <CardContent className="space-y-6">
              {/* DDN Upload */}
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 hover:border-violet-300 hover:bg-violet-50/30 transition-colors cursor-pointer" onClick={() => ddn.inputRef.current?.click()} data-testid="ddn-upload-area">
                <div className="flex items-center gap-4">
                  <FolderOpen className="w-10 h-10 text-slate-400" />
                  <div className="flex-1"><p className="font-medium text-slate-700">Select DDN Files</p><p className="text-sm text-slate-500">Click to upload one or more DDN XML files (multiple selection supported)</p></div>
                  <Upload className="w-5 h-5 text-slate-400" />
                </div>
              </div>
              {ddn.files.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">DDN Files ({ddn.files.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {ddn.files.map((file, i) => (
                      <span key={i} className="px-3 py-1 bg-violet-50 text-violet-700 rounded-full text-sm flex items-center gap-2">
                        <FileText className="w-3 h-3" />{file.filename}
                        <button onClick={(e) => { e.stopPropagation(); ddn.remove(i); }} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* DML Upload */}
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 hover:border-violet-300 hover:bg-violet-50/30 transition-colors cursor-pointer" onClick={() => dml.inputRef.current?.click()} data-testid="dml-upload-area">
                <div className="flex items-center gap-4">
                  <FileText className="w-10 h-10 text-slate-400" />
                  <div className="flex-1"><p className="font-medium text-slate-700">Select DML Files</p><p className="text-sm text-slate-500">Click to upload one or more DML XML files (multiple selection supported)</p></div>
                  <Upload className="w-5 h-5 text-slate-400" />
                </div>
              </div>
              {dml.files.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">DML Files ({dml.files.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {dml.files.map((file, i) => (
                      <span key={i} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm flex items-center gap-2">
                        <FileText className="w-3 h-3" />{file.filename}
                        <button onClick={(e) => { e.stopPropagation(); dml.remove(i); }} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* History */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><Clock className="w-5 h-5 text-slate-600" />History</CardTitle>
            <CardDescription>Recent comparisons</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingHistory ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : history.length === 0 ? (
              <div className="text-center py-8"><CheckCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-slate-500 text-sm">No comparison history yet</p></div>
            ) : (
              <ScrollArea className="h-[300px]">
                <div className="space-y-3">
                  {history.map((item) => (
                    <div key={item.id} className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors group" onClick={() => handleViewDetail(item)}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          {item.project_title && <p className="text-xs text-violet-600 font-medium truncate">{item.project_title}</p>}
                          <p className="text-sm text-slate-700">{item.ddn_file_count} DDN / {item.dml_file_count} DML</p>
                          <p className="text-xs text-slate-500">{new Date(item.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Badge variant={item.match_percentage >= 80 ? "default" : item.match_percentage >= 50 ? "secondary" : "destructive"}>{item.match_percentage}%</Badge>
                          <button onClick={(e) => { e.stopPropagation(); exportHistoryItem(item); }} className="opacity-0 group-hover:opacity-100 p-1 hover:text-violet-600 transition-opacity" title="Export Report"><Download className="w-4 h-4" /></button>
                          <button onClick={(e) => { e.stopPropagation(); deleteItem(item.id); }} className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-500 transition-opacity" title="Delete"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Inline Comparison Results */}
      {comparisonResult && (
        <Card className="border-2 border-violet-200" data-testid="comparison-results">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><Truck className="w-5 h-5 text-violet-600" />Comparison Results</CardTitle>
              <CardDescription>Analysis of DDN and DML file contents</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={handleDismissResults}><X className="w-4 h-4" /></Button>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Export Buttons */}
            <div className="space-y-2">
              <p className="text-sm font-semibold text-slate-700">Export Options</p>
              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => downloadExport('all', currentProjectName || selectedHistory?.project_title)} variant="outline" size="sm" className="text-xs" data-testid="export-all-btn"><Download className="w-3 h-3 mr-1" />Export All</Button>
                <Button onClick={() => downloadExport('ddn', currentProjectName || selectedHistory?.project_title)} variant="outline" size="sm" className="text-xs text-amber-700 border-amber-300 hover:bg-amber-50" disabled={!comparisonResult.in_ddn_only?.length} data-testid="export-ddn-btn"><Download className="w-3 h-3 mr-1" />Export DDN Only ({comparisonResult.in_ddn_only?.length || 0})</Button>
                <Button onClick={() => downloadExport('dml', currentProjectName || selectedHistory?.project_title)} variant="outline" size="sm" className="text-xs text-red-700 border-red-300 hover:bg-red-50" disabled={!comparisonResult.in_dml_only?.length} data-testid="export-dml-btn"><Download className="w-3 h-3 mr-1" />Export DML Only ({comparisonResult.in_dml_only?.length || 0})</Button>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="p-4 bg-slate-100 rounded-lg text-center"><p className="text-2xl font-bold text-slate-900">{comparisonResult.total_ddn}</p><p className="text-xs text-slate-600">DDN Entries</p></div>
              <div className="p-4 bg-slate-100 rounded-lg text-center"><p className="text-2xl font-bold text-slate-900">{comparisonResult.total_dml}</p><p className="text-xs text-slate-600">DML Entries</p></div>
              <div className="p-4 bg-green-100 rounded-lg text-center"><p className="text-2xl font-bold text-green-700">{comparisonResult.matches?.length || 0}</p><p className="text-xs text-green-600">Matches</p></div>
              <div className="p-4 bg-orange-100 rounded-lg text-center" data-testid="mismatch-count"><p className="text-2xl font-bold text-orange-700">{comparisonResult.mismatches?.length || 0}</p><p className="text-xs text-orange-600">Issue/Lang Mismatches</p></div>
              <div className="p-4 bg-violet-100 rounded-lg text-center"><p className="text-2xl font-bold text-violet-700">{comparisonResult.match_percentage}%</p><p className="text-xs text-violet-600">Match Rate</p></div>
            </div>

            {/* Issue / Language Mismatches */}
            {comparisonResult.mismatches?.length > 0 && (
              <div className="border rounded-lg" data-testid="mismatch-panel">
                <div className="p-3 bg-orange-50 border-b flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-orange-600" /><span className="font-medium text-orange-800">Issue / Language Mismatches ({comparisonResult.mismatches.length})</span><span className="text-xs text-orange-600 ml-2">DMC matched, but issue number, in-work, language or country differs</span></div>
                <ScrollArea className="h-[150px] p-3"><div className="space-y-1">{comparisonResult.mismatches.map((entry, i) => <p key={i} className="text-sm text-slate-700 font-mono">{entry}</p>)}</div></ScrollArea>
              </div>
            )}

            {/* Mismatches */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded-lg">
                <div className="p-3 bg-amber-50 border-b flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /><span className="font-medium text-amber-800">In DDN Only ({comparisonResult.in_ddn_only?.length || 0})</span></div>
                <ScrollArea className="h-[200px] p-3">{comparisonResult.in_ddn_only?.length > 0 ? <div className="space-y-1">{comparisonResult.in_ddn_only.map((entry, i) => <p key={i} className="text-sm text-slate-700 font-mono">{entry}</p>)}</div> : <p className="text-sm text-slate-500 text-center py-4">No mismatches</p>}</ScrollArea>
              </div>
              <div className="border rounded-lg">
                <div className="p-3 bg-red-50 border-b flex items-center gap-2"><FileX className="w-4 h-4 text-red-600" /><span className="font-medium text-red-800">In DML Only ({comparisonResult.in_dml_only?.length || 0})</span></div>
                <ScrollArea className="h-[200px] p-3">{comparisonResult.in_dml_only?.length > 0 ? <div className="space-y-1">{comparisonResult.in_dml_only.map((entry, i) => <p key={i} className="text-sm text-slate-700 font-mono">{entry}</p>)}</div> : <p className="text-sm text-slate-500 text-center py-4">No mismatches</p>}</ScrollArea>
              </div>
            </div>

            {/* Matches */}
            <div className="border rounded-lg">
              <div className="p-3 bg-green-50 border-b flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-600" /><span className="font-medium text-green-800">Matching Entries ({comparisonResult.matches?.length || 0})</span></div>
              <ScrollArea className="h-[150px] p-3">{comparisonResult.matches?.length > 0 ? <div className="grid grid-cols-2 gap-1">{comparisonResult.matches.map((entry, i) => <p key={i} className="text-sm text-slate-700 font-mono truncate">{entry}</p>)}</div> : <p className="text-sm text-slate-500 text-center py-4">No matches found</p>}</ScrollArea>
            </div>
          </CardContent>
        </Card>
      )}
      <DataPurgeDialog open={showPurgeDialog} onAcknowledge={() => setShowPurgeDialog(false)} toolName="comparison" />
    </div>
  );
};

export default DeliveryPage;
